import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';

import { Tenant } from '../../../types/tenants';
import { useTenantsRequests } from '../../../api/tenants';
import styles from './editTenant.module.css';
import { useSnackbar } from 'notistack';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../consts';

interface EditTenantProps {
  tenant?: Tenant;
  onSuccess?: () => void;
}

type FormData = {
  name: string;
  pesel: string;
  nip: string;
  regon: string;
  krs: string;
  logo?: File | null;
  website: string;
};

const defaultFormData: FormData = {
  name: '',
  pesel: '',
  nip: '',
  regon: '',
  krs: '',
  logo: undefined,
  website: '',
};

export const EditTenant: React.FC<EditTenantProps> = ({ tenant, onSuccess }) => {
  const { t } = useTranslation();
  const { updateFormData, error, loading } = useTenantsRequests();
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        pesel: tenant.pesel || '',
        nip: tenant.nip || '',
        regon: tenant.regon || '',
        krs: tenant.krs || '',
        logo: undefined,
        website: tenant.website || '',
      });
    }
  }, [tenant]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setFormData((prevState) => ({
      ...prevState,
      logo: file,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tenant?.id) {
      return;
    }

    const { success } = await updateFormData({
      id: tenant.id,
      ...formData,
    });

    if (success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      onSuccess?.();
      enqueueSnackbar(t("Workspace updated successfully"), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Workspace update failed"), ERROR_SNACKBAR);
    }
  };

  if (!tenant) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="name"
          label={t('Workspace name')}
          size="small"
          fullWidth
          value={formData.name}
          onChange={handleInputChange}
          required
        />
        {error?.name?.[0]?.message && <div className={styles.errorMessage}>{error?.name?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="pesel"
          label={t('PESEL')}
          size="small"
          fullWidth
          value={formData.pesel}
          onChange={handleInputChange}
        />
        {error?.pesel?.[0]?.message && <div className={styles.errorMessage}>{error?.pesel?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="nip"
          label={t('NIP')}
          size="small"
          fullWidth
          value={formData.nip}
          onChange={handleInputChange}
        />
        {error?.nip?.[0]?.message && <div className={styles.errorMessage}>{error?.nip?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="regon"
          label={t('REGON')}
          size="small"
          fullWidth
          value={formData.regon}
          onChange={handleInputChange}
        />
        {error?.regon?.[0]?.message && <div className={styles.errorMessage}>{error?.regon?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="krs"
          label={t('KRS')}
          size="small"
          fullWidth
          value={formData.krs}
          onChange={handleInputChange}
        />
        {error?.krs?.[0]?.message && <div className={styles.errorMessage}>{error?.krs?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="website"
          label={t('Website')}
          size="small"
          fullWidth
          value={formData.website}
          onChange={handleInputChange}
        />
        {error?.website?.[0]?.message && <div className={styles.errorMessage}>{error?.website?.[0]?.message}</div>}
      </Box>

      <Box sx={{ width: "100%" }}>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
        {error?.logo?.[0]?.message && <div className={styles.errorMessage}>{error?.logo?.[0]?.message}</div>}
      </Box>

      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          type="submit"
          disabled={loading}
        >
          {loading ? t('Saving...') : t('Update workspace')}
        </Button>
      </Box>
    </Box>
  );
};
